/**
 * The open scenario's follow-ups for the broker, one at a time above the composer, as on the
 * scenario page's Ask box. The page owns them: every change is posted back to it, and it checks
 * the scenario again with the answers.
 */
import { createContext, useContext, useEffect, useRef, useState } from "react";
import LendyIcon from "../components/lendy/LendyIcon";

const ANSWER_MAX_LENGTH = 2000;
const LETTERS = ["A", "B", "C", "D"];

/** { shared, send } from Chat.js: what the page sent, and how to post back to it. */
export const ScenarioQuestionsContext = createContext(null);

export default function ScenarioQuestionsTray({ scenarioId }) {
  const context = useContext(ScenarioQuestionsContext);
  const shared = context?.shared;
  const [progress, setProgress] = useState(shared?.progress);
  // The follow-up whose Other was opened; a typed answer that isn't an option opens it too.
  const [otherQuestion, setOtherQuestion] = useState(null);
  const otherRef = useRef(null);

  // The page's own changes (a new check, the answers sent) replace what is here; an echo of
  // this tray's change does not, so typing is never overwritten mid-word.
  useEffect(() => {
    if (shared && (shared.progressSource === "page" || !progress)) {
      setProgress(shared.progress);
    }
  }, [shared]); // eslint-disable-line

  if (!shared || !progress || shared.scenarioId !== scenarioId || !shared.questions.length) {
    return null;
  }

  const { questions, isBusy, dismissingQuestion } = shared;
  const step = Math.min(progress.step, questions.length - 1);
  const question = questions[step];
  const nextQuestion = questions[step + 1];
  const isLast = step === questions.length - 1;
  const draft = progress.drafts[question] || "";
  const canAdvance = draft.trim().length > 0 && !isBusy;
  const choices = shared.options?.[question] || [];
  const isOtherOpen = choices.length > 0 && (otherQuestion === question || (draft.length > 0 && !choices.includes(draft)));

  const post = (value) => context.send({ scenarioId, ...value });

  const update = (change) => {
    const next = { ...progress, step, ...change };
    setProgress(next);
    post({ type: "progress", progress: next });
  };

  const advance = (drafts) => {
    if (isLast) {
      post({ type: "submit", progress: { ...progress, step, drafts } });
      return;
    }
    update({ drafts, step: step + 1 });
  };

  const setDraft = (text) => update({ drafts: { ...progress.drafts, [question]: text } });

  // As in Cursor and Claude Code, an answer picked is the answer: on to the next follow-up.
  // The last one only selects, as Check again runs the check.
  const pick = (option) => {
    setOtherQuestion(null);
    const drafts = { ...progress.drafts, [question]: option };
    update(isLast ? { drafts } : { drafts, step: step + 1 });
  };

  // Up and down move between the answers, as in Claude Code; Enter picks the one focused.
  const moveFocus = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const rows = Array.from(e.currentTarget.querySelectorAll('[role="radio"], input'));
    const current = rows.indexOf(document.activeElement);
    const next = e.key === "ArrowDown" ? current + 1 : current - 1;
    e.preventDefault();
    rows[Math.min(Math.max(next, 0), rows.length - 1)]?.focus();
  };

  const openOther = () => {
    setOtherQuestion(question);
    if (choices.includes(draft)) setDraft("");
    setTimeout(() => otherRef.current?.focus(), 0);
  };

  const closeOther = () => {
    setOtherQuestion(null);
    setDraft("");
  };

  const skip = () => {
    const { [question]: _skipped, ...drafts } = progress.drafts;
    advance(drafts);
  };

  const onKeyDown = (e) => {
    if (e.target.tagName === "TEXTAREA" || e.target.tagName === "INPUT") return;
    // Enter on a focused answer or button is that button's own click.
    if (e.key === "Enter" && e.target.tagName === "BUTTON") return;
    const letter = LETTERS.indexOf(e.key.toUpperCase());
    if (choices.length > 0 && letter !== -1 && letter <= choices.length && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      if (letter === choices.length) openOther();
      else pick(choices[letter]);
    } else if (e.key === "Enter" && canAdvance) {
      e.preventDefault();
      advance(progress.drafts);
    } else if (e.key === "ArrowRight" && !isLast) update({ step: step + 1 });
    else if (e.key === "ArrowLeft" && step > 0) update({ step: step - 1 });
  };

  return (
    <div className="aav2-questions" role="region" aria-label="Follow-up questions" onKeyDown={onKeyDown}>
      <div className="aav2-questions-head">
        <p className="aav2-questions-title">Questions</p>
        <div className="aav2-questions-pager">
          {questions.length > 1 && (
            <>
              <button type="button" className="aav2-questions-page" aria-label="Previous question" disabled={step === 0} onClick={() => update({ step: step - 1 })}>
                <LendyIcon name="left" size={14} />
              </button>
              <span className="aav2-questions-count">
                {step + 1} of {questions.length}
              </span>
              <button type="button" className="aav2-questions-page" aria-label="Next question" disabled={isLast} onClick={() => update({ step: step + 1 })}>
                <LendyIcon name="right" size={14} />
              </button>
            </>
          )}
          <button
            type="button"
            className={`aav2-questions-page aav2-questions-collapse${progress.isCollapsed ? " is-collapsed" : ""}`}
            aria-label={progress.isCollapsed ? "Expand questions" : "Collapse questions"}
            aria-expanded={!progress.isCollapsed}
            onClick={() => update({ isCollapsed: !progress.isCollapsed })}
          >
            <LendyIcon name="down" size={14} />
          </button>
        </div>
      </div>

      {progress.isCollapsed ? (
        <button type="button" className="aav2-questions-summary" onClick={() => update({ isCollapsed: false })}>
          {question}
        </button>
      ) : (
        <div className="aav2-questions-body">
          <p className="aav2-questions-prompt">{question}</p>
          {choices.length > 0 ? (
            <div className="aav2-questions-choices" role="radiogroup" aria-label={question} onKeyDown={moveFocus}>
              {choices.map((option, index) => {
                const selected = !isOtherOpen && draft === option;
                return (
                  <button
                    key={option}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`aav2-questions-choice${selected ? " is-selected" : ""}`}
                    onClick={() => pick(option)}
                  >
                    <span className="aav2-questions-letter" aria-hidden="true">
                      {LETTERS[index]}
                    </span>
                    <span>{option}</span>
                  </button>
                );
              })}
              {isOtherOpen ? (
                <div className="aav2-questions-choice is-selected is-other">
                  <span className="aav2-questions-letter" aria-hidden="true">
                    {LETTERS[choices.length]}
                  </span>
                  <input
                    ref={otherRef}
                    className="aav2-questions-other"
                    value={draft}
                    maxLength={ANSWER_MAX_LENGTH}
                    aria-label={`Your answer to: ${question}`}
                    placeholder="What you already know"
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (canAdvance) advance(progress.drafts);
                      } else if (e.key === "Escape") closeOther();
                    }}
                  />
                </div>
              ) : (
                <button type="button" role="radio" aria-checked={false} className="aav2-questions-choice" onClick={openOther}>
                  <span className="aav2-questions-letter" aria-hidden="true">
                    {LETTERS[choices.length]}
                  </span>
                  <span className="aav2-questions-other-label">Other...</span>
                </button>
              )}
            </div>
          ) : (
          <textarea
            rows={2}
            className="aav2-questions-answer"
            value={draft}
            maxLength={ANSWER_MAX_LENGTH}
            aria-label={`Your answer to: ${question}`}
            placeholder="What you already know, e.g. Gift letter is on file"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (canAdvance) advance(progress.drafts);
              }
            }}
          />
          )}
          {nextQuestion && (
            <button type="button" className="aav2-questions-next" onClick={() => update({ step: step + 1 })}>
              {nextQuestion}
            </button>
          )}
          <div className="aav2-questions-foot">
            <p className="aav2-questions-hint" aria-hidden="true">
              {choices.length > 0 ? "↑↓ to choose · ↵ to select" : "↵ to continue · ⇧↵ new line"}
            </p>
            <button
              type="button"
              className="aav2-questions-quiet"
              title="Not needed: later checks won't ask it again"
              disabled={isBusy}
              onClick={() => post({ type: "dismiss", question })}
            >
              {dismissingQuestion === question ? "Dismissing…" : "Not needed"}
            </button>
            <button type="button" className="aav2-questions-quiet" disabled={isBusy} onClick={skip}>
              Skip
            </button>
            <button type="button" className="aav2-questions-go" disabled={!canAdvance} onClick={() => advance(progress.drafts)}>
              {isLast ? "Check again" : "Next"}
              <LendyIcon name="enter" size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
